import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import './App.css';

const { FiPhone, FiMail, FiMapPin, FiClock, FiCheck, FiStar, FiArrowRight, FiPlay, FiGlobe, FiAlertCircle, FiSend, FiDownload, FiFileText, FiImage, FiGift, FiCamera, FiAward, FiUsers } = FiIcons;
const { FaWhatsapp, FaGraduationCap, FaSchool, FaBuilding, FaCalendarAlt } = FaIcons;

// Asset URLs
const ASSETS = {
  LOGO: "https://cdn1.sharemyimage.com/smi/2025/12/14/CN-LOGO_ROUND.png",
  MASCOT: "https://cdn1.sharemyimage.com/smi/2025/12/14/CN-CAPATIN-MASCOT.png",
  DOME: "https://cdn1.sharemyimage.com/smi/2025/12/14/Capatain-Nova-Mascot-logos-11-2.png",
  SOLAR_SYSTEM: "https://cdn1.sharemyimage.com/smi/2025/12/14/solar-system-with-planets.jpg"
};

function App() {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    phone: '',
    email: '',
    city: '',
    eventType: 'School',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const whatsappNumber = "918500182765";
  const recipientEmail = "captainnovadome@gmail.com";
  
  const schoolMessage = encodeURIComponent(
    "Hello Captain Nova! I would like to book the Dome Planetarium for our school. Please share available dates and pricing."
  );

  const exhibitionMessage = encodeURIComponent(
    "Hello Captain Nova! I am interested in booking the Dome Planetarium for an exhibition or public event. Please share details."
  );

  // Updated Brochure and Flyer Links
  const flyerLink = "https://sharemyimage.com/image/NEW-poster-with-Border.oFiEKc";
  const brochureLink = "https://go.fliplink.me/view/Captainnova-Brochure";

  const shows = [
    { name: "Journey Through the Solar System", description: "Explore planets, moons, and asteroids in our cosmic neighborhood", icon: "🪐" },
    { name: "Mission to the Moon", description: "Experience the Apollo missions and lunar exploration", icon: "🌙" },
    { name: "Dinosaur Adventure", description: "Travel back in time to meet prehistoric giants", icon: "🦕" },
    { name: "Galaxies & Milky Way", description: "Discover our galaxy and billions of others beyond", icon: "🌌" },
    { name: "Satellites in the Sky", description: "Learn about artificial satellites and space technology", icon: "🛰️" },
    { name: "Ocean World", description: "Dive deep into Earth's mysterious underwater realms", icon: "🌊" },
    { name: "Young Astronauts", description: "Perfect introduction to space for younger students", icon: "👨‍🚀" },
    { name: "Incredible Earth", description: "Explore our planet's climate, weather, and natural wonders", icon: "🌍" },
    { name: "Night Sky & Constellations", description: "Navigate the stars and learn ancient constellation stories", icon: "⭐" },
    { name: "Aurora Lights", description: "Witness the magical Northern and Southern Lights", icon: "🌌" },
    { name: "Animated Kids Shows", description: "Engaging animated content specially designed for young learners", icon: "🎬" },
    { name: "Story Telling & Short Stories", description: "Captivating stories brought to life in the dome", icon: "📖" },
    { name: "Moral Stories", description: "Educational tales teaching important life values", icon: "💡" }
  ];

  const extraActivities = [
    {
      title: "Fun Learning Activities",
      description: "Interactive learning stations that make science exciting and accessible for all ages",
      icon: "🎓",
      color: "from-blue-500 to-cyan-500"
    },
    {
      title: "Feature Zones",
      description: "Explore various themed zones including Galaxy Hub, Space Station, and Discovery Corner",
      icon: "🚀",
      color: "from-purple-500 to-pink-500"
    },
    {
      title: "Selfie Zone & Galaxy Hub",
      description: "Capture memorable moments with space-themed backdrops and AR experiences",
      icon: "📸",
      color: "from-orange-500 to-red-500"
    },
    {
      title: "School Competitions",
      description: "Quiz contests, drawing competitions, and science challenges with exciting prizes",
      icon: "🏆",
      color: "from-green-500 to-teal-500"
    },
    {
      title: "Personalized Print Zone",
      description: "Get custom-printed certificates, photos, and personalized space-themed merchandise",
      icon: "🖨️",
      color: "from-indigo-500 to-blue-500"
    },
    {
      title: "Keepsakes & Memorabilia",
      description: "Take home special mementos from your cosmic adventure",
      icon: "🎁",
      color: "from-pink-500 to-rose-500"
    },
    {
      title: "CN Achievement Badges",
      description: "Earn exclusive Captain Nova badges and certificates for participation and winning",
      icon: "🏅",
      color: "from-yellow-500 to-orange-500"
    },
    {
      title: "Scratch Card Discounts",
      description: "Win incredible discounts on future bookings with our special scratch card promotions!",
      icon: "🎟️",
      color: "from-red-500 to-pink-500"
    }
  ];

  const gamesAndPrizes = [
    {
      title: "Interactive Space Games",
      description: "Engage in fun space-themed games and challenges",
      icon: "🎮",
      features: ["Virtual Reality Experiences", "AR Space Missions", "Quiz Challenges", "Puzzle Stations"]
    },
    {
      title: "Print & Selfie Zones",
      description: "Capture and print your cosmic memories",
      icon: "📷",
      features: ["Professional Photo Booths", "Green Screen Technology", "Instant Photo Printing", "Digital Photo Sharing"]
    },
    {
      title: "Exciting Prizes",
      description: "Win amazing rewards and recognition",
      icon: "🎁",
      features: ["Achievement Certificates", "Captain Nova Badges", "Educational Kits", "Special Discount Vouchers"]
    }
  ];

  const testimonials = [
    {
      name: "Dr. Priya Sharma",
      title: "Principal, Delhi Public School",
      text: "The dome planetarium was absolutely magical! Our students were completely engaged throughout the 30-minute show. It's the perfect blend of education and entertainment.",
      rating: 5
    },
    {
      name: "Rajesh Kumar",
      title: "Science Teacher, St. Mary's School",
      text: "Captain Nova's presentation made complex astronomical concepts so easy to understand. The visual experience is unforgettable. Highly recommended!",
      rating: 5
    },
    {
      name: "Meera Patel",
      title: "Event Coordinator, Science Fair Mumbai",
      text: "Professional setup, engaging content, and excellent coordination. The dome was the highlight of our science exhibition. Will definitely book again!",
      rating: 5
    }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const emailMessage = `
NEW LEAD SUBMISSION FROM CAPTAIN NOVA WEBSITE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

👤 Contact Information:
   Name: ${formData.name}
   Organization: ${formData.organization}
   
📞 Contact Details:
   Phone: ${formData.phone}
   Email: ${formData.email}
   
📍 Location:
   City: ${formData.city}
   
🎯 Event Type:
   ${formData.eventType}
   
💬 Message:
   ${formData.message || 'No additional message provided'}
   
⏰ Submitted:
   ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Please respond to this inquiry within 24 hours.
      `;

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: 'fd57957a-35a0-43e0-83a8-5d8f2f2ad73f',
          subject: `🚀 New Lead: ${formData.eventType} - ${formData.organization}`,
          from_name: formData.name,
          from_email: formData.email,
          message: emailMessage,
          replyto: formData.email,
          redirect: false
        })
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            organization: '',
            phone: '',
            email: '',
            city: '',
            eventType: 'School',
            message: ''
          });
        }, 5000);
      } else {
        throw new Error(result.message || 'Failed to send');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError('Failed to send your request. Please try contacting us via WhatsApp or email directly.');
      
      const mailtoSubject = encodeURIComponent(`Lead from ${formData.name} - ${formData.organization}`);
      const mailtoBody = encodeURIComponent(
        `NEW LEAD SUBMISSION\n\n` +
        `Name: ${formData.name}\n` +
        `Organization: ${formData.organization}\n` +
        `Phone: ${formData.phone}\n` +
        `Email: ${formData.email}\n` +
        `City: ${formData.city}\n` +
        `Event Type: ${formData.eventType}\n` +
        `Message: ${formData.message}\n\n` +
        `Submitted: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}`
      );
      
      window.location.href = `mailto:${recipientEmail}?subject=${mailtoSubject}&body=${mailtoBody}`;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 text-white overflow-x-hidden font-sans">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-stars opacity-30"></div>
        
        <div 
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: `url(${ASSETS.SOLAR_SYSTEM})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>

        <div className="relative z-10 text-center max-w-6xl mx-auto flex flex-col items-center">
          <motion.img
            src={ASSETS.LOGO}
            alt="Captain Nova Logo"
            className="w-32 md:w-48 mb-6 drop-shadow-2xl"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, type: "spring" }}
          />

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8 relative"
          >
            <div className="inline-block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent text-lg font-semibold mb-4 tracking-wider uppercase">
              Captain Nova Dome Planetarium
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-shadow">
              360° Dome Planetarium Experience
              <span className="block text-3xl md:text-4xl text-blue-300 mt-4">
                at Your School
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed drop-shadow-md">
              Immersive space, dinosaur, ocean, and science shows for KG–12 students. Bringing the universe directly to your classroom.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 max-w-4xl mx-auto w-full"
          >
            <div className="flex flex-col items-center justify-center p-4 bg-white/5 rounded-xl backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors">
              <span className="text-3xl mb-2">🏠</span>
              <span className="text-sm font-medium">Portable Dome</span>
            </div>
            <div className="flex flex-col items-center justify-center p-4 bg-white/5 rounded-xl backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors">
              <SafeIcon icon={FiClock} className="text-3xl text-blue-400 mb-2" />
              <span className="text-sm font-medium">30-min Shows</span>
            </div>
            <div className="flex flex-col items-center justify-center p-4 bg-white/5 rounded-xl backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors">
              <span className="text-3xl mb-2">🧪</span>
              <span className="text-sm font-medium">STEM Education</span>
            </div>
            <div className="flex flex-col items-center justify-center p-4 bg-white/5 rounded-xl backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors">
              <span className="text-3xl mb-2">🗣️</span>
              <span className="text-sm font-medium">English & Hindi</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col items-center w-full relative space-y-6"
          >
            <motion.img
              src={ASSETS.MASCOT}
              alt="Captain Nova Mascot"
              className="hidden lg:block absolute -left-48 bottom-0 w-48 xl:w-64 drop-shadow-2xl float"
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
            />

            <div className="flex flex-col md:flex-row gap-6 justify-center items-center w-full">
              <a
                href={`https://wa.me/${whatsappNumber}?text=${schoolMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-green-500 hover:bg-green-600 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center space-x-3 shadow-lg hover:shadow-green-500/50 w-full md:w-auto justify-center"
              >
                <SafeIcon icon={FaSchool} className="text-2xl" />
                <span>Book for Schools</span>
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
              </a>

              <a
                href={`https://wa.me/${whatsappNumber}?text=${exhibitionMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-purple-600 hover:bg-purple-700 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center space-x-3 shadow-lg hover:shadow-purple-600/50 w-full md:w-auto justify-center"
              >
                <SafeIcon icon={FaBuilding} className="text-xl" />
                <span>Book for Events</span>
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full max-w-2xl">
              <a
                href={flyerLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 px-6 py-3 rounded-full font-semibold text-base transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 shadow-lg hover:shadow-orange-500/40 w-full sm:w-auto justify-center border-2 border-white/10"
              >
                <SafeIcon icon={FiImage} className="text-xl" />
                <span>View Flyer</span>
                <SafeIcon icon={FiDownload} className="text-sm group-hover:translate-y-0.5 transition-transform" />
              </a>

              <a
                href={brochureLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 px-6 py-3 rounded-full font-semibold text-base transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 shadow-lg hover:shadow-cyan-500/40 w-full sm:w-auto justify-center border-2 border-white/10"
              >
                <SafeIcon icon={FiFileText} className="text-xl" />
                <span>View Brochure</span>
                <SafeIcon icon={FiArrowRight} className="text-sm group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Is the Dome Planetarium Section */}
      <section className="py-24 px-4 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">What Is the Dome Planetarium?</h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Captain Nova's portable digital dome transforms any school hall into a breathtaking 360° immersive learning theater. Students don't just learn about science – they experience it firsthand.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {[
                { icon: FiPlay, color: "bg-blue-500", title: "360° Full-Dome Projection", desc: "Immersive visuals surround students completely, creating an unforgettable learning experience." },
                { icon: null, emoji: "🔊", color: "bg-purple-500", title: "Surround Sound System", desc: "Crystal-clear audio enhances the visual experience with realistic sound effects." },
                { icon: FiClock, color: "bg-green-500", title: "Quick Setup (25 minutes)", desc: "Professional setup and breakdown with minimal disruption to your school schedule." },
                { icon: null, emoji: "🛡️", color: "bg-red-500", title: "Safe Indoor Structure", desc: "Inflatable dome made from fire-retardant materials, completely safe for students." }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-5">
                  <div className={`${item.color} rounded-2xl p-4 flex-shrink-0 shadow-lg`}>
                    {item.icon ? (
                      <SafeIcon icon={item.icon} className="text-2xl text-white" />
                    ) : (
                      <span className="text-2xl">{item.emoji}</span>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10 group">
                <img
                  src={ASSETS.DOME}
                  alt="Inflatable Dome Planetarium"
                  className="w-full h-auto object-contain transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-8">
                  <div className="grid grid-cols-2 gap-4 text-sm font-medium">
                    <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/10">
                      <div className="text-blue-300 text-xs uppercase mb-1">Space Required</div>
                      <div>18×18 ft minimum</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/10">
                      <div className="text-blue-300 text-xs uppercase mb-1">Height Needed</div>
                      <div>12 ft ceiling</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/10">
                      <div className="text-blue-300 text-xs uppercase mb-1">Capacity</div>
                      <div>25-40 students</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/10">
                      <div className="text-blue-300 text-xs uppercase mb-1">Shows/Day</div>
                      <div>Up to 15 batches</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-10 -right-10 w-full h-full bg-blue-600/20 rounded-full blur-3xl -z-10"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* NEW: Watch the Experience Section */}
      <section className="py-24 px-4 bg-black/30 relative">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Watch the Experience</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Get a glimpse of the magic that awaits your students inside the dome.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative aspect-video w-full max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-slate-800 group"
          >
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
              <div className="text-center p-8">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300 cursor-pointer">
                  <SafeIcon icon={FiPlay} className="text-3xl text-white ml-2" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Demo Video Coming Soon</h3>
                <p className="text-gray-400">Contact us on WhatsApp for a video preview!</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Shows & Experiences Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative">
        <div 
          className="absolute top-0 left-0 right-0 h-96 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `url(${ASSETS.SOLAR_SYSTEM})`,
            backgroundPosition: 'top center',
            backgroundSize: 'cover'
          }}
        ></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Shows & Experiences</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Choose from our collection of stunning 30-minute immersive shows, designed to inspire and educate.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {shows.map((show, index) => (
              <motion.div
                key={show.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="group bg-white/5 backdrop-blur-sm border border-white/5 rounded-2xl p-6 hover:bg-white/10 hover:border-blue-500/30 transition-all duration-300 transform hover:-translate-y-2 shadow-lg hover:shadow-2xl"
              >
                <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300 filter drop-shadow-lg">
                  {show.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-blue-300 transition-colors">
                  {show.name}
                </h3>
                <p className="text-gray-400 text-sm mb-4 leading-relaxed line-clamp-3">
                  {show.description}
                </p>
                <div className="flex items-center text-sm text-blue-400 font-medium pt-4 border-t border-white/5">
                  <SafeIcon icon={FiClock} className="mr-2" />
                  <span>30 minutes</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW: Extra Activities & Features Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-stars opacity-10"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Beyond the Dome Experience</h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              We offer much more than just planetarium shows! Make your event unforgettable with exciting activities, interactive zones, and special features designed to maximize student engagement and learning.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {extraActivities.map((activity, index) => (
              <motion.div
                key={activity.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-blue-500/30 transition-all duration-300 transform hover:-translate-y-2 shadow-lg"
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${activity.color} flex items-center justify-center text-3xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  {activity.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-blue-300 transition-colors">
                  {activity.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {activity.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Games, Play & Prizes Subsection */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mt-20"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Games, Play & Prizes
            </h3>

            <div className="grid md:grid-cols-3 gap-8">
              {gamesAndPrizes.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 rounded-2xl p-8 shadow-2xl hover:shadow-blue-500/20 transition-all duration-300"
                >
                  <div className="text-6xl mb-6 text-center">{item.icon}</div>
                  <h4 className="text-2xl font-bold mb-4 text-center text-blue-300">
                    {item.title}
                  </h4>
                  <p className="text-gray-400 text-center mb-6">
                    {item.description}
                  </p>
                  <ul className="space-y-3">
                    {item.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-3">
                        <SafeIcon icon={FiCheck} className="text-green-400 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Highlight Banner */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-3xl p-8 md:p-12 text-center shadow-2xl"
          >
            <div className="flex items-center justify-center mb-4">
              <SafeIcon icon={FiGift} className="text-5xl mr-4" />
              <h3 className="text-3xl md:text-4xl font-bold">Special Promotion!</h3>
            </div>
            <p className="text-xl md:text-2xl mb-6 leading-relaxed max-w-3xl mx-auto">
              Get incredible discounts with our exclusive <strong>Online Scratch Cards</strong> available during the event!
            </p>
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full border border-white/30">
              <span className="text-4xl">🎟️</span>
              <span className="font-bold text-lg">Limited Time Offer!</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who Is It For Section */}
      <section className="py-24 px-4 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Who Is It For?</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our dome planetarium is perfect for various educational institutions and events across India.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: FaSchool, title: "Schools (KG–12)", desc: "Primary and secondary schools looking to enhance STEM education" },
              { icon: FaGraduationCap, title: "Colleges & Universities", desc: "Higher education institutions for astronomy and science programs" },
              { icon: FaBuilding, title: "Event Organizers", desc: "Science fairs, exhibitions, and educational events" },
              { icon: FaCalendarAlt, title: "Public Programs", desc: "Museums, NGOs, and government educational initiatives" }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center group p-6 rounded-2xl hover:bg-white/5 transition-colors duration-300"
              >
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-500/20">
                  <SafeIcon icon={item.icon} className="text-3xl text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-blue-400 transition-colors">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Schools Choose Us Section */}
      <section className="py-24 px-4 bg-gradient-to-r from-blue-900 to-purple-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Why Schools Choose Us</h2>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Trusted by hundreds of schools across India for delivering exceptional educational experiences.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Fits in a single hall (18×18 ft)",
              "25–40 students per show",
              "10–15 batches per day",
              "STEM-aligned learning outcomes",
              "Perfect fun + education balance",
              "Safe and fully supervised",
              "Memorable student experience",
              "Professional setup team",
              "Flexible scheduling options"
            ].map((benefit, index) => (
              <motion.div
                key={benefit}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-4 bg-white/10 backdrop-blur-md rounded-xl p-5 hover:bg-white/20 transition-all duration-300 border border-white/10"
              >
                <div className="bg-green-500/20 p-2 rounded-full">
                  <SafeIcon icon={FiCheck} className="text-green-400 text-lg" />
                </div>
                <span className="text-lg font-medium">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">How It Works</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Simple, hassle-free process from booking to amazing educational experience.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Contact Us", desc: "School contacts us via phone, WhatsApp, or our contact form", icon: "📞" },
              { step: "2", title: "Schedule Date", desc: "We coordinate and confirm the best date and time for your event", icon: "📅" },
              { step: "3", title: "Setup & Shows", desc: "Our team arrives, sets up the dome, and conducts immersive shows", icon: "🎬" },
              { step: "4", title: "Mission Complete", desc: "Students enjoy unforgettable experience, we pack up and leave", icon: "🚀" }
            ].map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center relative"
              >
                {index < 3 && (
                  <div className="hidden md:block absolute top-12 left-full w-full h-1 bg-gradient-to-r from-blue-500/30 to-purple-500/30 transform translate-x-4"></div>
                )}
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl w-24 h-24 flex items-center justify-center mx-auto mb-6 shadow-xl shadow-blue-500/20 rotate-3 hover:rotate-6 transition-transform">
                  <span className="text-4xl">{step.icon}</span>
                </div>
                <div className="inline-block bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                  STEP {step.step}
                </div>
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm px-4">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-4 bg-slate-800/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">What Educators Say</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Hear from schools and educators who have experienced the magic.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-slate-800 border border-slate-700 rounded-2xl p-8 shadow-xl relative"
              >
                <div className="absolute top-6 right-8 text-6xl text-slate-700 font-serif opacity-50">"</div>
                <div className="flex mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-lg fill-current mr-1" />
                  ))}
                </div>
                <p className="text-gray-300 mb-8 leading-relaxed relative z-10">
                  {testimonial.text}
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 flex items-center justify-center text-white font-bold text-lg mr-3">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-lg">{testimonial.name}</div>
                    <div className="text-blue-400 text-sm">{testimonial.title}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing & Booking CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-blue-900 to-purple-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-stars opacity-20"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Book?</h2>
            <p className="text-xl text-gray-200 mb-10 leading-relaxed">
              Flexible pricing available for schools, colleges, and events. Special discounts for multi-day bookings and NGO programs.
            </p>

            <div className="grid sm:grid-cols-3 gap-6 mb-12">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/10">
                <div className="text-3xl mb-3">💰</div>
                <h3 className="font-bold mb-2">Flexible Pricing</h3>
                <p className="text-sm text-gray-300">Per-student or full-day options</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/10">
                <div className="text-3xl mb-3">🎯</div>
                <h3 className="font-bold mb-2">Group Discounts</h3>
                <p className="text-sm text-gray-300">Special rates for multiple schools</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/10">
                <div className="text-3xl mb-3">🤝</div>
                <h3 className="font-bold mb-2">NGO Support</h3>
                <p className="text-sm text-gray-300">Reduced pricing for social causes</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <a
                href={`https://wa.me/${whatsappNumber}?text=${schoolMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-green-500 hover:bg-green-600 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg hover:shadow-green-500/30"
              >
                <SafeIcon icon={FaWhatsapp} className="text-2xl" />
                <span>Get Pricing</span>
              </a>
              <a
                href="#contact"
                className="group bg-white text-slate-900 hover:bg-gray-100 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg"
              >
                <SafeIcon icon={FaCalendarAlt} className="text-xl" />
                <span>Check Availability</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact & Lead Capture Form Section */}
      <section id="contact" className="py-24 px-4 bg-slate-900">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Contact & Booking</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Ready to bring the universe to your school? Fill out the form below and we'll get back to you within 24 hours.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 bg-slate-800 rounded-3xl p-8 md:p-12 shadow-2xl border border-slate-700">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <h3 className="text-2xl font-bold mb-6 text-white">Get in Touch</h3>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4 group">
                  <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                    <SafeIcon icon={FiPhone} className="text-xl" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-400 text-sm uppercase tracking-wide">Phone</div>
                    <div className="text-white text-lg font-medium">+91 85001 82765</div>
                  </div>
                </div>

                <div className="flex items-center space-x-4 group">
                  <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                    <SafeIcon icon={FiMail} className="text-xl" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-400 text-sm uppercase tracking-wide">Email</div>
                    <div className="text-white text-lg font-medium">captainnovadome@gmail.com</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4 group">
                  <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-colors flex-shrink-0">
                    <SafeIcon icon={FiMapPin} className="text-xl" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-400 text-sm uppercase tracking-wide">Address</div>
                    <div className="text-white text-lg font-medium">
                      Taranagar, Serilingampalli,<br/>Hyderabad, India-500019
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4 group">
                  <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center text-green-400 group-hover:bg-green-500 group-hover:text-white transition-colors">
                    <SafeIcon icon={FaWhatsapp} className="text-xl" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-400 text-sm uppercase tracking-wide">WhatsApp</div>
                    <div className="text-white text-lg font-medium">Quick responses available</div>
                  </div>
                </div>
              </div>

              <div className="pt-8 flex justify-center lg:justify-start">
                <img src={ASSETS.LOGO} alt="Captain Nova" className="h-16 opacity-50" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {isSubmitted ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-8 bg-green-500/10 rounded-2xl border border-green-500/30">
                  <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-green-500/30">
                    <SafeIcon icon={FiCheck} className="text-4xl text-white" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4 text-white">Thank You!</h3>
                  <p className="text-gray-300 text-lg">
                    We've received your inquiry and sent the details to <strong>captainnovadome@gmail.com</strong>. We'll contact you within 24 hours to discuss your dome planetarium booking.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your Name *"
                      required
                      disabled={isSubmitting}
                      className="w-full p-4 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all disabled:opacity-50"
                    />
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      placeholder="School / Organization *"
                      required
                      disabled={isSubmitting}
                      className="w-full p-4 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all disabled:opacity-50"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Phone Number *"
                      required
                      disabled={isSubmitting}
                      className="w-full p-4 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all disabled:opacity-50"
                    />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address *"
                      required
                      disabled={isSubmitting}
                      className="w-full p-4 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all disabled:opacity-50"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="City *"
                      required
                      disabled={isSubmitting}
                      className="w-full p-4 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all disabled:opacity-50"
                    />
                    <select
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-full p-4 rounded-xl bg-slate-900 border border-slate-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all appearance-none disabled:opacity-50"
                    >
                      <option value="School">School Program</option>
                      <option value="Exhibition">Exhibition / Fair</option>
                      <option value="College">College / University</option>
                      <option value="Other">Other Event</option>
                    </select>
                  </div>

                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your event, preferred dates, number of students, etc."
                    rows={4}
                    disabled={isSubmitting}
                    className="w-full p-4 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all resize-none disabled:opacity-50"
                  ></textarea>

                  {submitError && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start space-x-3">
                      <SafeIcon icon={FiAlertCircle} className="text-red-400 text-xl flex-shrink-0 mt-0.5" />
                      <p className="text-red-300 text-sm">{submitError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 py-4 rounded-xl font-bold text-lg text-white shadow-lg shadow-blue-600/30 transition-all duration-300 transform hover:translate-y-[-2px] flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiSend} className="text-xl" />
                        <span>Send Request</span>
                      </>
                    )}
                  </button>

                  <p className="text-center text-gray-400 text-xs mt-4">
                    By submitting this form, your inquiry will be sent to <strong className="text-blue-400">captainnovadome@gmail.com</strong>
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-16 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-1">
              <img src={ASSETS.LOGO} alt="Captain Nova" className="w-24 mb-6 opacity-80" />
              <p className="text-gray-400 mb-6 leading-relaxed text-sm">
                Bringing the wonders of the universe directly to your school hall. Inspiring the next generation of scientists and explorers.
              </p>
              <div className="flex space-x-4">
                <a
                  href={`https://wa.me/${whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-green-400 hover:bg-green-500 hover:text-white transition-all"
                >
                  <SafeIcon icon={FaWhatsapp} className="text-xl" />
                </a>
                <a
                  href="mailto:captainnovadome@gmail.com"
                  className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-blue-400 hover:bg-blue-500 hover:text-white transition-all"
                >
                  <SafeIcon icon={FiMail} className="text-xl" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-white mb-6">Contact Info</h4>
              <ul className="space-y-4 text-gray-400 text-sm">
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiPhone} className="text-blue-500 mt-0.5" />
                  <span>+91 85001 82765</span>
                </li>
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiMail} className="text-blue-500 mt-0.5" />
                  <span>captainnovadome@gmail.com</span>
                </li>
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiGlobe} className="text-blue-500 mt-0.5" />
                  <span>captainnova.in</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-white mb-6">Address</h4>
              <ul className="space-y-4 text-gray-400 text-sm">
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiMapPin} className="text-blue-500 mt-0.5" />
                  <span>
                    Taranagar, Serilingampalli,<br/>
                    Hyderabad, India-500019
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-white mb-6">Quick Links</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="#contact" className="hover:text-blue-400 transition-colors">Book Now</a></li>
                <li><a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">WhatsApp Chat</a></li>
                <li><a href={flyerLink} target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">View Flyer</a></li>
                <li><a href={brochureLink} target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">View Brochure</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Captain Nova Dome Planetarium. All rights reserved. | Bringing the Universe to Every Classroom</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={`https://wa.me/${whatsappNumber}?text=${schoolMessage}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 shadow-2xl transition-all duration-300 transform hover:scale-110 animate-bounce group"
      >
        <SafeIcon icon={FaWhatsapp} className="text-3xl text-white group-hover:rotate-12 transition-transform" />
        <span className="absolute -top-10 right-0 bg-white text-slate-900 text-xs font-bold px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg">Chat with us!</span>
      </a>
    </div>
  );
}

export default App;